<template>
  <v-dialog
    :value="value"
    max-width="600"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="headline">
        <v-icon
          left
          color="primary"
        >
          mdi-gift-outline
        </v-icon>
        {{ $t($ts.giftCardDialogs.redeemTitle) }}
        <v-spacer />
        <v-btn
          icon
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-form
          ref="form"
          lazy-validation
        >
          <v-text-field
            v-model="codeInput"
            :label="$t($ts.giftCardDialogs.code)"
            prepend-inner-icon="mdi-pound"
            :disabled="isLoading"
            outlined
            dense
            hide-details="auto"
            @keydown.enter.prevent="handleLookup"
          >
            <template #append-outer>
              <v-btn
                icon
                small
                @click="toggleScanner"
              >
                <v-icon>{{ showScanner ? "mdi-camera-off" : "mdi-qrcode-scan" }}</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </v-form>

        <v-expand-transition>
          <div
            v-if="showScanner"
            class="mt-4"
          >
            <v-alert
              type="info"
              dense
              text
              class="mb-2"
            >
              {{ $t($ts.giftCardDialogs.scanHelpRedeem) }}
            </v-alert>
            <qrcode-stream
              @decode="handleScan"
              @init="handleScannerInit"
            />
          </div>
        </v-expand-transition>

        <v-progress-linear
          v-if="isLoading"
          indeterminate
          color="primary"
          class="mt-4"
        />

        <v-slide-y-transition>
          <v-card
            v-if="card"
            outlined
            class="mt-4 pa-4"
          >
            <div class="d-flex align-center mb-2">
              <v-chip
                small
                :color="statusColor(card.status)"
                text-color="white"
                class="mr-2"
              >
                {{ statusLabel(card.status) }}
              </v-chip>
              <span class="subtitle-2">{{ card.code }}</span>
            </div>
            <div class="text--secondary">
              <div>{{ card.giftCardType?.name || $t($ts.giftCardDialogs.giftCard) }}</div>
              <div>{{ $t($ts.giftCardDialogs.balance) }}: <strong>{{ formatCurrency(card.balance, card.currency) }}</strong></div>
            </div>
          </v-card>
        </v-slide-y-transition>

        <v-text-field
          v-if="card"
          v-model.number="amount"
          :label="$t($ts.giftCardDialogs.amountToUse)"
          type="number"
          :max="maxAmount || card.balance"
          outlined
          dense
          class="mt-4"
          :rules="amountRules"
        />

        <v-textarea
          v-model="notes"
          :label="$t($ts.giftCardDialogs.notesOptional)"
          outlined
          auto-grow
          rows="2"
          class="mt-2"
        />
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          text
          @click="close"
        >
          {{ $t($ts.giftCardDialogs.cancel) }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!canConfirm"
          @click="confirm"
        >
          <v-icon left>
            mdi-check
          </v-icon>
          {{ $t($ts.giftCardDialogs.redeemTitle) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { GiftCardRedemptionChannel, GiftCardStatus, IGiftCardDTO } from "@shared/types";
import { giftCardsModule } from "@/store/modules/giftCards/giftCardsModule";
import { snackbarModule } from "@/store/modules/snackbar/snackbarModule";

@Component({
  name: "gift-card-redeem-dialog",
})
export default class GiftCardRedeemDialog extends Vue {
  @Prop({ type: Boolean, default: false }) readonly value!: boolean;
  @Prop({ type: Number, default: 0 }) readonly maxAmount!: number;
  @Prop({ type: String, default: "" }) readonly initialCode!: string;

  public codeInput = "";
  public showScanner = false;
  public isLoading = false;
  public card: IGiftCardDTO | null = null;
  public amount = 0;
  public notes = "";

  get amountRules() {
    return [
      (v: number) => v > 0 || this.$t(this.$ts.giftCardDialogs.amountMustBePositive).toString(),
      (v: number) =>
        (this.card ? v <= this.card.balance : true) || this.$t(this.$ts.giftCardDialogs.cannotExceedBalance).toString(),
      (v: number) =>
        (this.maxAmount ? v <= this.maxAmount : true) || this.$t(this.$ts.giftCardDialogs.cannotExceedTotal).toString(),
    ];
  }

  get canConfirm(): boolean {
    return !!this.card && this.amount > 0 && (!this.maxAmount || this.amount <= this.maxAmount);
  }

  @Watch("value")
  onDialogChanged(open: boolean) {
    if (open) {
      this.reset();
      if (this.initialCode) {
        this.codeInput = this.initialCode;
        this.handleLookup();
      }
    }
  }

  @Watch("initialCode")
  onInitialCodeChange(newCode: string) {
    if (this.value && newCode) {
      this.codeInput = newCode;
      this.handleLookup();
    }
  }

  public reset(): void {
    this.codeInput = "";
    this.card = null;
    this.amount = 0;
    this.notes = "";
    this.showScanner = false;
  }

  public toggleScanner(): void {
    this.showScanner = !this.showScanner;
  }

  public async handleLookup(): Promise<void> {
    if (!this.codeInput.trim()) return;
    await this.lookupCard(this.codeInput.trim());
  }

  public async lookupCard(rawCode: string): Promise<void> {
    const normalized = this.extractCode(rawCode);
    if (!normalized) {
      snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.invalidCode).toString());
      return;
    }
    this.codeInput = normalized;
    this.isLoading = true;
    try {
      const result = await giftCardsModule.lookupByCode(normalized);
      if (!result) return;
      // The server only redeems Active cards; a partially used card stays Active until its balance is 0.
      if (result.status !== GiftCardStatus.Active) {
        snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.cannotBeUsed).toString());
        return;
      }
      if (result.balance <= 0) {
        snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.noBalance).toString());
        return;
      }
      this.card = result;
      this.amount = Math.min(result.balance, this.maxAmount || result.balance);
    } finally {
      this.isLoading = false;
    }
  }

  public handleScannerInit(promise: Promise<void>): void {
    promise.catch(() => {
      snackbarModule.postErrorSnackbarMessage(this.$t(this.$ts.giftCardDialogs.cameraUnavailable).toString());
      this.showScanner = false;
    });
  }

  public async handleScan(content: string): Promise<void> {
    this.showScanner = false;
    await this.lookupCard(content);
  }

  public extractCode(value: string): string {
    try {
      const maybeUrl = new URL(value);
      const codeParam = maybeUrl.searchParams.get("code");
      return codeParam ? codeParam.toUpperCase() : maybeUrl.pathname.replace("/redeem", "").split("/").pop()?.toUpperCase() || "";
    } catch (error) {
      return value.replace(/\s+/g, "").toUpperCase();
    }
  }

  public statusLabel(status: GiftCardStatus): string {
    const statuses = this.$ts.giftCardManagement.cards.statuses;
    const labels: Record<GiftCardStatus, string> = {
      [GiftCardStatus.Generated]: statuses.generated,
      [GiftCardStatus.Printed]: statuses.printed,
      [GiftCardStatus.Active]: statuses.active,
      [GiftCardStatus.Redeemed]: statuses.redeemed,
      [GiftCardStatus.PartiallyRedeemed]: statuses.partiallyRedeemed,
      [GiftCardStatus.Expired]: statuses.expired,
      [GiftCardStatus.Voided]: statuses.voided,
    };
    return labels[status] ? this.$t(labels[status]).toString() : "";
  }

  public statusColor(status: GiftCardStatus): string {
    switch (status) {
      case GiftCardStatus.Active:
        return "green";
      case GiftCardStatus.Generated:
      case GiftCardStatus.Printed:
        return "blue";
      case GiftCardStatus.Redeemed:
        return "success";
      case GiftCardStatus.Expired:
      case GiftCardStatus.Voided:
        return "grey";
      default:
        return "primary";
    }
  }

  public formatCurrency(value: number, currency: string | undefined): string {
    const formatter = new Intl.NumberFormat(this.$i18n.locale, {
      style: "currency",
      currency: currency || "NOK",
    });
    return formatter.format(value);
  }

  public confirm(): void {
    if (!this.card) return;
    this.$emit("confirm", {
      code: this.card.code,
      amount: this.amount,
      channel: GiftCardRedemptionChannel.Pos,
      notes: this.notes,
    });
    this.close();
  }

  public close(): void {
    this.reset();
    this.$emit("input", false);
  }
}
</script>

<style scoped lang="scss">
.v-alert {
  border-radius: 8px;
}
</style>
